import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Optional left-aligned icon (lucide-react icon, etc.) */
  leftIcon?: React.ReactNode;
  /** Optional right-aligned icon or content */
  rightSlot?: React.ReactNode;
  /** Visual size — default is the typical 36px form height */
  inputSize?: "sm" | "default" | "lg";
}

const sizeMap: Record<NonNullable<InputProps["inputSize"]>, string> = {
  sm:      "h-8 text-xs px-2.5",
  default: "h-9 text-sm px-3",
  lg:      "h-10 text-sm px-3.5",
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", leftIcon, rightSlot, inputSize = "default", ...props }, ref) => {
    // iOS/WKWebView renders native date/time controls at an intrinsic width
    // that ignores `w-full`, so they come out wider than sibling inputs.
    // `appearance-none` strips the native sizing and makes them respect the
    // container (the picker still opens on tap). Applies to every date/time type.
    const isDateLike = ["date", "datetime-local", "time", "month", "week"].includes(type);
    const baseInput = cn(
      "w-full min-w-0 bg-surface text-fg placeholder:text-fg-subtle",
      "border border-border rounded-md",
      "transition-[border-color,box-shadow,background-color] duration-[var(--dur-fast)] ease-[var(--ease-out)]",
      "outline-none",
      "hover:border-border-strong",
      "focus:border-primary focus:ring-2 focus:ring-ring/30",
      "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-subtle",
      "aria-invalid:border-danger aria-invalid:focus:ring-danger/30",
      "file:bg-transparent file:border-0 file:font-medium file:text-fg",
      // Browsers paint their own clear "x" inside a text/search field —
      // Chromium's ::-webkit-search-cancel-button (appears on hover/focus of a
      // non-empty type="search") and Edge's ::-ms-clear. Both double up with
      // the clear button we render in `rightSlot`, so suppress them here
      // rather than at each call site.
      "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
      "[&::-ms-clear]:hidden",
      isDateLike && "appearance-none",
      sizeMap[inputSize],
    );

    if (!leftIcon && !rightSlot) {
      return (
        <input
          ref={ref}
          type={type}
          data-slot="input"
          className={cn(baseInput, className)}
          {...props}
        />
      );
    }

    return (
      <div className={cn("relative flex items-center w-full", className)}>
        {leftIcon ? (
          <span
            aria-hidden
            className="absolute left-2.5 inline-flex items-center text-fg-subtle [&_svg]:size-4 pointer-events-none"
          >
            {leftIcon}
          </span>
        ) : null}
        <input
          ref={ref}
          type={type}
          data-slot="input"
          className={cn(
            baseInput,
            leftIcon && "pl-9",
            rightSlot && "pr-9",
          )}
          {...props}
        />
        {rightSlot ? (
          <span className="absolute right-2 inline-flex items-center text-fg-subtle">
            {rightSlot}
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
